import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosInstance } from "axios";
import {
  createGroup as apiCreateGroup,
  deleteGroup as apiDeleteGroup,
  listGroups as apiListGroups,
  updateGroup as apiUpdateGroup,
} from "settings/account/components/NotificationsV2/requests/request";
import { NotificationGroup } from "settings/account/components/NotificationsV2/types";

interface GroupsState {
  groups: NotificationGroup[];
  loading: boolean;
  error: string | null;
  selectedGroupId: string | null;
}

const initialState: GroupsState = {
  groups: [],
  loading: false,
  error: null,
  selectedGroupId: null,
};

export const fetchGroups = createAsyncThunk<
  NotificationGroup[],
  { api: AxiosInstance; tenantId: number },
  { rejectValue: string }
>(
  "notificationsV2Groups/fetchAll",
  async ({ api, tenantId }, { rejectWithValue }) => {
    try {
      const res = await apiListGroups(api, tenantId, ["counts"]);
      return res.data.data;
    } catch {
      return rejectWithValue("Failed to load groups");
    }
  }
);

export const createGroup = createAsyncThunk<
  NotificationGroup,
  {
    api: AxiosInstance;
    tenantId: number;
    body: {
      name: string;
      color?: string | null;
      description?: string | null;
      linkedTagId?: string | null;
    };
  },
  { rejectValue: string }
>(
  "notificationsV2Groups/create",
  async ({ api, tenantId, body }, { rejectWithValue }) => {
    try {
      const res = await apiCreateGroup(api, tenantId, body);
      return res.data.data;
    } catch {
      return rejectWithValue("Failed to create group");
    }
  }
);

export const updateGroup = createAsyncThunk<
  NotificationGroup,
  {
    api: AxiosInstance;
    tenantId: number;
    groupId: string;
    body: {
      name?: string;
      color?: string | null;
      description?: string | null;
      linkedTagId?: string | null;
    };
  },
  { rejectValue: string }
>(
  "notificationsV2Groups/update",
  async ({ api, tenantId, groupId, body }, { rejectWithValue }) => {
    try {
      const res = await apiUpdateGroup(api, tenantId, groupId, body);
      return res.data.data;
    } catch {
      return rejectWithValue("Failed to update group");
    }
  }
);

export const deleteGroup = createAsyncThunk<
  string,
  { api: AxiosInstance; tenantId: number; groupId: string },
  { rejectValue: string }
>(
  "notificationsV2Groups/delete",
  async ({ api, tenantId, groupId }, { rejectWithValue }) => {
    try {
      await apiDeleteGroup(api, tenantId, groupId);
      return groupId;
    } catch {
      return rejectWithValue("Failed to delete group");
    }
  }
);

const groupsSlice = createSlice({
  name: "notificationsV2Groups",
  initialState,
  reducers: {
    setSelectedGroupId: (state, action: PayloadAction<string | null>) => {
      state.selectedGroupId = action.payload;
    },
    clearGroups: (state) => {
      state.groups = [];
      state.error = null;
      state.selectedGroupId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGroups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = action.payload;
      })
      .addCase(fetchGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Failed to load groups";
      })
      .addCase(createGroup.fulfilled, (state, action) => {
        state.groups.push(action.payload);
      })
      .addCase(updateGroup.fulfilled, (state, action) => {
        const idx = state.groups.findIndex(
          (g) => g.id === action.payload.id
        );
        if (idx >= 0)
          state.groups[idx] = { ...state.groups[idx], ...action.payload };
      })
      .addCase(deleteGroup.fulfilled, (state, action) => {
        state.groups = state.groups.filter((g) => g.id !== action.payload);
        if (state.selectedGroupId === action.payload)
          state.selectedGroupId = null;
      });
  },
});

export const { setSelectedGroupId, clearGroups } = groupsSlice.actions;
export default groupsSlice.reducer;
